'use client'

import { useCallback, useEffect, useMemo, useState } from 'react'

import { PLTypedWidgetProps } from '@src/components/pl-base-widget'
import { useSetProductSelection } from '@src/components/pl-base-widget/contexts/page-widget-store'
import { useSetPdpSelectionDerived } from '@src/components/pl-base-widget/contexts/pdp-selection'
import { usePLPageMetaOptional } from '@src/components/pl-page-context'
import { STATSIG_GATE_NAMES, useStatsigGate } from '@src/experiments/statsig-integration/use-statsig-gate.hook'
import type SvdProduct from '@src/quince-site-common/src/domains/svd/svd-product'
import { setInventoryInSessionStorage } from '@src/quince-site-common/src/services/inventoryService/inventory.sessionStorage'
// The DS-switching wrapper — `shouldUseDesignSystem()` picks the DS or legacy variant per request, so the
// SVD bundle follows the DS ramp exactly like the V1 bundle. The SVD selection engine lives in the shared
// reducer/helpers both variants import, so this one widget covers both branches.
import BundleProductPurchase from '@src/quince-site-pdp/src/organisms/productPageSections/bundleProductPurchase/BundleProductPurchase'
import type { BundleSvdPublishState } from '@src/quince-site-pdp/src/organisms/productPageSections/bundleProductPurchase/BundleProductPurchase.types'
import type { TPDPSectionData } from '@src/quince-site-pdp/src/types/pdp-section-data.type'
import { getStoreProgramIdFromStorage } from '@src/utils/b2b/b2b.utils'

import { protoToSvdBundle } from './bundle-purchase-v2.adapter'
import { getSvdBundleInventorySeed } from './bundle-purchase-v2.inventory'
import styles from './bundle-purchase-v2.module.scss'

import type { BundlePurchaseV2WidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

/**
 * BUNDLE_PURCHASE_V2 widget — the SVD bundle buy box. The BE emits it instead of BUNDLE_PURCHASE on an
 * `enrichVersion=V2` bundle session.
 *
 * Deliberately a sibling of the V1 `bundle-purchase` widget rather than a mode inside it: the payloads have
 * no shape in common (id-slot lists into a `productDatas` map of full `ProductPurchaseV2WidgetData`, versus
 * three arrays of legacy items). What the two DO share is the underlying UI — both render the same
 * `BundleProductPurchase` organism, which now carries an SVD branch alongside its legacy one.
 */
function BundlePurchaseV2WidgetComponent(props: PLTypedWidgetProps<BundlePurchaseV2WidgetData>) {
  // `asBaseWidget` defaults `data` to `{}`, so treat the payload as possibly-absent despite the prop type.
  const { bundlePurchaseV2Data } = props.data as Partial<typeof props.data>
  if (!bundlePurchaseV2Data) return null
  // `key` ties the buy box's lifecycle to bundle identity — a client-side navigation to a different bundle
  // remounts it, re-seeding every slot's selection from the new payload (React's reset-state-with-a-key
  // pattern, same as the single-product V2 buy box).
  return <BundlePurchaseV2BuyBox key={bundlePurchaseV2Data.id} data={bundlePurchaseV2Data} />
}

function BundlePurchaseV2BuyBox({ data }: { data: BundlePurchaseV2WidgetData }) {
  const pageMeta = usePLPageMetaOptional()
  // The V2 wire carries no bundle `slug` (V1 did); the page path is the correct stand-in for the organism's
  // `pdpStateManager.setSelectedOptions` publish. See gap G1 in `master-plan.md`.
  const slug = pageMeta?.seoData?.slug ?? pageMeta?.pathname

  // Adapt once per payload. `bundle` expands the id lists into positional SLOTS (duplicates preserved);
  // `mergeProducts` is the set-swap sidecar; the two selection arrays stay index-aligned with the slots.
  const { bundle, mergeProducts, requiredSelections, addOnSelections } = useMemo(
    () => protoToSvdBundle(data, { slug }),
    [data, slug],
  )

  // Passing this object at all is what switches the organism to the SVD engine — see `getBundleInitialState`.
  const svdSeeds = useMemo(
    () => ({ required: requiredSelections, addOns: addOnSelections }),
    [requiredSelections, addOnSelections],
  )

  // The buy box only reads `selectedOptions` off `sectionData`; empty keeps its own seeding authoritative.
  const sectionData = useMemo(() => ({ selectedOptions: [] }) as unknown as TPDPSectionData, [])

  // Payload inventory seed — skips the mount-time per-product fan-out. Kill-switch + B2B bail-out mirror
  // the V1 bundle widget exactly. `undefined` (today's reality — the wire carries no `activeInventory`)
  // falls back to the client fetch.
  const forceClientInventory = useStatsigGate(STATSIG_GATE_NAMES.PL_FORCE_CLIENT_INVENTORY)
  const inventorySeed = useMemo(() => {
    if (forceClientInventory || getStoreProgramIdFromStorage()) return undefined
    // Only the initially-rendered slots — merge siblings are resolved on swap, not seeded (V1 parity).
    // Duplicate slots share one product, and the seed is keyed by product id, so de-duping is implicit.
    // The `Bundle` domain types its children as `Product`; the adapter built every one of them as an
    // `SvdProduct`, which is what carries the per-variant `activeInventory` this seed reads.
    return getSvdBundleInventorySeed([
      ...(bundle.customBundleProducts ?? []),
      ...(bundle.addOnBundleProducts ?? []),
    ] as unknown as SvdProduct[])
  }, [bundle, forceClientInventory])

  useEffect(() => {
    if (!inventorySeed) return
    setInventoryInSessionStorage({ data: inventorySeed.inventoryMap, code: 200 })
  }, [inventorySeed])

  // --- Publishes for sibling widgets -------------------------------------------------------------
  // The V1 bundle mirrors redux to reach the PL store (`use-bundle-selection-bridge`) because the legacy buy
  // box exposes no callback. V2 gets the state pushed directly through `onBundleSvdStateChange`, which the
  // organism fires whenever its SVD state changes.
  const publishPdpSelectionDerived = useSetPdpSelectionDerived()
  const setSelection = useSetProductSelection()
  // `useState` (not `useRef`) so the first publish is retained even if it arrives before the effects below;
  // both setters are documented stable, so this callback is stable and cannot re-arm the organism's effect.
  const [, setPublishedOnce] = useState(false)

  const handleBundleSvdStateChange = useCallback(
    (state: BundleSvdPublishState) => {
      publishPdpSelectionDerived({
        // Presence of `bundleSelections` is what switches TRANSPARENT_PRICING / BRAND_COMPARISON to their
        // bundle branches — the same contract the V1 bridge publishes.
        bundleSelections: state.childSelections,
        quincePrice: state.quincePrice,
        traditionalPrice: state.traditionalPrice,
        recoProductId: state.recoProductId,
        // `recoSlug` is intentionally omitted: it exists so PRODUCT_GALLERY can target the first child's
        // slug-scoped image experiment, and on a V2 bundle media belongs to BUNDLE_GALLERY_V2 (which does
        // its own VDA matching) — plus the V2 wire carries no constituent slug at all (gap G1).
      })
      // Publish the anchor subset even when EMPTY: `setSelection` always flips `isSelectionAuthoritative`,
      // which is what releases the reco fetchers' hold. An anchor-less bundle must release them, not deadlock.
      setSelection(state.anchorSelection)
      setPublishedOnce(true)
    },
    [publishPdpSelectionDerived, setSelection],
  )

  return (
    // `styles.host` collapses the LEGACY section's gallery column via `[class*='…']` selectors — PL renders
    // media as its own widget. Those selectors can't match the DS variant's Panda atomic class names, so the
    // DS branch drops its gallery through the typed `hideBundleImages` prop instead (host wrapper is inert).
    <div className={styles.host}>
      <BundleProductPurchase
        bundle={bundle}
        mergeProducts={mergeProducts}
        sectionData={sectionData}
        svdSeeds={svdSeeds}
        onBundleSvdStateChange={handleBundleSvdStateChange}
        // Recos are their own PL widgets; the buy box renders none inline.
        recommendedContentfulProductIds={undefined}
        mteId={pageMeta?.pathname}
        purchaseInfoSeed={inventorySeed?.purchaseInfo}
        // The buy box renders ProductDetails + BrandComparison inline on desktop; PL ships both as their own
        // widgets (ACCORDION / PRODUCT_SUMMARY / BRAND_COMPARISON), so the inline copies would duplicate them.
        hideInlineSections
        // DS-branch twin of the `styles.host` gallery collapse (BUNDLE_GALLERY_V2 owns media in PL).
        hideBundleImages
      />
    </div>
  )
}

export { BundlePurchaseV2WidgetComponent }
